import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, ShieldCheck, X, Loader2, Building2 } from "lucide-react";

/**
 * AdminNonprofits
 * ---------------
 * Admin-only queue for reviewing pending nonprofit verification requests.
 * Shows each submitter's:
 *   - name / email / role / business_name
 *   - EIN + self-declared org name
 *   - any uploaded documents (rendered inline via data URL)
 *
 * Actions:
 *   - Approve   → flips user to `verified` + unlocks 50% Pro discount
 *   - Reject    → asks for a reason, saved to `nonprofit_rejection_reason`
 */
export default function AdminNonprofits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/nonprofits/pending");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/nonprofits/${id}/approve`);
      toast.success("Approved — nonprofit benefits unlocked.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
    }
  };

  const reject = async (id) => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Please write a short reason (min 3 chars).");
      return;
    }
    try {
      await api.post(`/admin/nonprofits/${id}/reject`, { reason: reason.trim() });
      toast.success("Rejected.");
      setRejectingId(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10" data-testid="admin-nonprofits-page">
      <Link to="/admin" className="text-sm underline" style={{ color: "var(--avt-purple)" }}>
        ← Admin home
      </Link>
      <div
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full ml-4"
        style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A", border: "1.5px solid #84CC16" }}
      >
        <Heart className="h-3 w-3" fill="#84CC16" strokeWidth={0} /> Nonprofit review
      </div>
      <h1 className="font-serif text-4xl mt-3">Pending nonprofit verifications</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
        Review manually-submitted proofs (usually IRS determination letters).
        EIN-verified submissions have already been auto-approved and don&rsquo;t show up here.
      </p>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="admin-nonprofits-empty"
          className="mt-10 card-warm rounded-xl p-8 text-center"
        >
          <ShieldCheck className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--avt-lime)" }} />
          <div className="font-serif text-xl">Queue is empty</div>
          <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
            No pending nonprofit reviews. New submissions will land here.
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`admin-nonprofit-row-${it.id}`}
              className="card-warm rounded-xl p-5"
            >
              <div className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="inline-flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4" style={{ color: "var(--avt-purple)" }} />
                    <strong className="truncate">{it.nonprofit_org_name || "(no org name)"}</strong>
                    <span
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: "rgba(107,47,186,0.10)", color: "var(--avt-purple)" }}
                    >
                      {it.role}
                    </span>
                  </div>
                  <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                    {it.name} · {it.email} · EIN {it.nonprofit_ein || "(none)"} ·
                    method: {it.nonprofit_verification_method || "document"}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    onClick={() => approve(it.id)}
                    data-testid={`admin-nonprofit-approve-${it.id}`}
                    className="btn-primary px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setRejectingId(rejectingId === it.id ? null : it.id); setReason(""); }}
                    data-testid={`admin-nonprofit-reject-${it.id}`}
                    className="rounded-full text-sm inline-flex items-center gap-1.5"
                    size="sm"
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>

              {it.nonprofit_documents?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {it.nonprofit_documents.map((d, i) => (
                    <a
                      key={i}
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-nonprofit-doc-${it.id}-${i}`}
                      className="text-xs underline"
                      style={{ color: "var(--avt-teal)" }}
                    >
                      📎 {d.filename || `document-${i + 1}`}
                    </a>
                  ))}
                </div>
              )}

              {rejectingId === it.id && (
                <div className="mt-4" data-testid={`admin-nonprofit-reject-form-${it.id}`}>
                  <Textarea
                    placeholder="Reason (visible to the submitter)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="text-sm"
                    rows={2}
                    data-testid={`admin-nonprofit-reject-reason-${it.id}`}
                  />
                  <Button
                    onClick={() => reject(it.id)}
                    data-testid={`admin-nonprofit-reject-confirm-${it.id}`}
                    variant="outline"
                    className="mt-2 rounded-full text-sm"
                    size="sm"
                  >
                    Confirm rejection
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
